import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Download, 
  Search, 
  User, 
  Clock, 
  Activity,
  LogIn,
  LogOut,
  AlertCircle,
  Edit,
  Trash2,
  Plus,
  Eye,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeamento de ícones por tipo de evento
const eventIcons = {
  login: LogIn,
  logout: LogOut,
  login_failed: AlertCircle,
  document_create: Plus,
  document_update: Edit,
  document_delete: Trash2,
  document_view: Eye,
  item_create: Plus,
  item_update: Edit,
  item_delete: Trash2,
  item_duplicate: Plus,
  certificate_upload: Plus,
  certificate_delete: Trash2,
  verification_create: Plus,
  verification_update: Edit,
  verification_delete: Trash2,
  verification_type_create: Plus,
  verification_type_update: Edit,
  verification_type_delete: Trash2,
  employee_create: Plus,
  employee_update: Edit,
  employee_delete: Trash2,
  employee_doc_upload: Plus,
  employee_doc_delete: Trash2,
  pharmacy_update: Edit,
  logo_upload: Plus,
  logo_delete: Trash2,
  data_cleanup: Trash2,
  envio_create: Plus,
  envio_update: Edit,
  envio_delete: Trash2,
  envio_marked: Activity,
  setor_create: Plus,
  setor_delete: Trash2,
  categoria_create: Plus,
  categoria_delete: Trash2,
  ticket_create: Plus,
  ticket_update: Edit,
  user_invite: Plus,
  user_remove: Trash2,
};

// Cores dos badges por tipo de evento
const eventColors = {
  login: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  logout: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
  login_failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  document_create: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  document_update: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  document_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  item_create: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  item_update: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  item_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  item_duplicate: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
  certificate_upload: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  certificate_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  verification_create: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
  verification_update: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  verification_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  verification_type_create: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
  verification_type_update: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  verification_type_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  employee_create: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  employee_update: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  employee_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  employee_doc_upload: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  employee_doc_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  pharmacy_update: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  logo_upload: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  logo_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  data_cleanup: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  envio_create: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  envio_update: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  envio_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  envio_marked: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  setor_create: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  setor_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  categoria_create: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  categoria_delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  ticket_create: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  ticket_update: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  user_invite: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  user_remove: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

const eventTypeOptions = [
  { value: '', label: 'Todos os eventos' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'login_failed', label: 'Login falhou' },
  { value: 'document_create', label: 'Documento criado' },
  { value: 'document_update', label: 'Documento atualizado' },
  { value: 'document_delete', label: 'Documento excluido' },
  { value: 'item_create', label: 'Item criado' },
  { value: 'item_update', label: 'Item atualizado' },
  { value: 'item_delete', label: 'Item excluido' },
  { value: 'item_duplicate', label: 'Item duplicado' },
  { value: 'certificate_upload', label: 'Certificado enviado' },
  { value: 'certificate_delete', label: 'Certificado removido' },
  { value: 'verification_create', label: 'Verificacao registrada' },
  { value: 'verification_delete', label: 'Verificacao excluida' },
  { value: 'verification_type_create', label: 'Tipo verificacao criado' },
  { value: 'verification_type_update', label: 'Tipo verificacao atualizado' },
  { value: 'verification_type_delete', label: 'Tipo verificacao excluido' },
  { value: 'employee_create', label: 'Funcionario cadastrado' },
  { value: 'employee_update', label: 'Funcionario atualizado' },
  { value: 'employee_delete', label: 'Funcionario excluido' },
  { value: 'employee_doc_upload', label: 'Doc. funcionario enviado' },
  { value: 'employee_doc_delete', label: 'Doc. funcionario removido' },
  { value: 'logo_upload', label: 'Logomarca enviada' },
  { value: 'logo_delete', label: 'Logomarca removida' },
  { value: 'envio_create', label: 'Envio criado' },
  { value: 'envio_update', label: 'Envio atualizado' },
  { value: 'envio_delete', label: 'Envio excluido' },
  { value: 'envio_marked', label: 'Envio marcado como realizado' },
  { value: 'setor_create', label: 'Setor criado' },
  { value: 'setor_delete', label: 'Setor excluido' },
  { value: 'categoria_create', label: 'Categoria criada' },
  { value: 'categoria_delete', label: 'Categoria excluida' },
  { value: 'ticket_create', label: 'Ticket de suporte criado' },
  { value: 'user_invite', label: 'Usuario convidado' },
  { value: 'user_remove', label: 'Usuario removido' },
  { value: 'data_cleanup', label: 'Limpeza de dados' },
];

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [filters, setFilters] = useState({
    event_type: '',
    start_date: '',
    end_date: '',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    skip: 0
  });
  const [activeTab, setActiveTab] = useState('logs');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const token = localStorage.getItem('token');

  const fetchLogs = async (skipValue = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', pagination.limit);
      params.append('skip', skipValue);

      const response = await fetch(`${API_URL}/api/auditoria/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setPagination(prev => ({ ...prev, total: data.total, skip: skipValue }));
      }
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auditoria/estatisticas?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  const fetchLoginHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auditoria/logins?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLoginHistory(data.logs);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico de logins:', error);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams();
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetch(`${API_URL}/api/auditoria/relatorio/pdf?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio_auditoria_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Relatório PDF gerado com sucesso!');
      } else {
        throw new Error('Erro ao gerar PDF');
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar relatório PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
    fetchLoginHistory();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'logins') {
      fetchLoginHistory();
    }
  }, [activeTab]);

  const handleSearch = () => {
    fetchLogs(0);
  };

  const handleNextPage = () => {
    const newSkip = pagination.skip + pagination.limit;
    if (newSkip < pagination.total) {
      fetchLogs(newSkip);
    }
  };

  const handlePrevPage = () => {
    const newSkip = Math.max(0, pagination.skip - pagination.limit);
    fetchLogs(newSkip);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventIcon = (eventType) => {
    const Icon = eventIcons[eventType] || Activity;
    return <Icon className="w-4 h-4" />;
  };

  const getEventColor = (eventType) => {
    return eventColors[eventType] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="space-y-6" data-testid="audit-log-container">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            Auditoria e Logs
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Histórico de atividades para fiscalização
          </p>
        </div>
        <Button 
          onClick={downloadPdf} 
          disabled={downloadingPdf}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloadingPdf ? 'Gerando...' : 'Exportar PDF'}
        </Button>
      </div>

      {/* Estatísticas */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-600" />
                <div>
                  <p className="text-2xl font-bold">{statistics.total_events}</p>
                  <p className="text-xs text-slate-500">Eventos (30 dias)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <LogIn className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{statistics.logins_count}</p>
                  <p className="text-xs text-slate-500">Logins (30 dias)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{statistics.active_users?.length || 0}</p>
                  <p className="text-xs text-slate-500">Usuários ativos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{statistics.period_days}</p>
                  <p className="text-xs text-slate-500">Dias analisados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Todos os Logs
          </TabsTrigger>
          <TabsTrigger value="logins" className="flex items-center gap-2">
            <LogIn className="w-4 h-4" />
            Histórico de Acessos
          </TabsTrigger>
        </TabsList>

        {/* Tab: Todos os Logs */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label className="text-sm">Tipo de Evento</Label>
                  <Select
                    value={filters.event_type}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, event_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os eventos" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value || "all"}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Data Inicial</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Data Final</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
                <Button onClick={handleSearch} variant="outline">
                  <Search className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
                <Button 
                  onClick={() => { setFilters({ event_type: '', start_date: '', end_date: '' }); fetchLogs(0); }}
                  variant="ghost"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Registros de Auditoria</CardTitle>
              <CardDescription>
                Mostrando {logs.length} de {pagination.total} registros
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Carregando...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Nenhum registro encontrado
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, index) => {
                    const isExpanded = expandedLog === (log._id || index);
                    const hasDetails = log.old_values || log.new_values || (log.changes && log.changes.length > 0) || log.details;
                    
                    return (
                    <div 
                      key={log._id || index}
                      className={`p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${hasDetails ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetails && setExpandedLog(isExpanded ? null : (log._id || index))}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full ${getEventColor(log.event_type)}`}>
                          {getEventIcon(log.event_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800 dark:text-slate-200">
                              {log.event_description || log.event_type}
                            </span>
                            {log.entity_name && (
                              <Badge variant="outline" className="text-xs">
                                {log.entity_name}
                              </Badge>
                            )}
                            {hasDetails && (
                              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500">
                                {isExpanded ? 'Recolher' : 'Ver detalhes'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {log.user_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(log.timestamp)}
                            </span>
                          </div>
                          
                          {/* Detalhes expandidos */}
                          {isExpanded && (
                            <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-md text-xs space-y-2 border border-slate-200 dark:border-slate-700" onClick={(e) => e.stopPropagation()}>
                              {log.details && (
                                <div>
                                  <span className="font-semibold text-slate-600 dark:text-slate-300">Detalhes: </span>
                                  <span className="text-slate-500">{typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details)}</span>
                                </div>
                              )}
                              {log.old_values && typeof log.old_values === 'object' && Object.keys(log.old_values).length > 0 && (
                                <div>
                                  <span className="font-semibold text-red-600">Valores anteriores:</span>
                                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                    {Object.entries(log.old_values).filter(([k]) => !['_id','created_by','farmacia_id','created_at','updated_at','historico_envios','certificados','documentos','senha_hash'].includes(k)).map(([key, val]) => (
                                      <div key={key} className="flex gap-1">
                                        <span className="font-medium text-slate-500">{key}:</span>
                                        <span className="text-red-500 truncate">{val === null || val === undefined ? '-' : typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {log.new_values && typeof log.new_values === 'object' && Object.keys(log.new_values).length > 0 && (
                                <div>
                                  <span className="font-semibold text-green-600">Valores novos:</span>
                                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                    {Object.entries(log.new_values).filter(([k]) => !['_id','created_by','farmacia_id','created_at','updated_at','historico_envios','certificados','documentos','senha_hash'].includes(k)).map(([key, val]) => (
                                      <div key={key} className="flex gap-1">
                                        <span className="font-medium text-slate-500">{key}:</span>
                                        <span className="text-green-600 truncate">{val === null || val === undefined ? '-' : typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {log.changes && Array.isArray(log.changes) && log.changes.length > 0 && (
                                <div>
                                  <span className="font-semibold text-slate-600 dark:text-slate-300">Campos alterados:</span>
                                  <div className="mt-1 space-y-1">
                                    {log.changes.map((change, ci) => (
                                      <div key={ci} className="flex items-center gap-2">
                                        <span className="font-medium text-slate-500">{change.field}:</span>
                                        <span className="text-red-500 line-through">{change.old === null || change.old === undefined ? '-' : typeof change.old === 'object' ? JSON.stringify(change.old) : String(change.old)}</span>
                                        <span className="text-slate-400">&rarr;</span>
                                        <span className="text-green-600">{change.new === null || change.new === undefined ? '-' : typeof change.new === 'object' ? JSON.stringify(change.new) : String(change.new)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );})}
                </div>
              )}

              {/* Paginação */}
              {pagination.total > pagination.limit && (
                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={pagination.skip === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </Button>
                  <span className="text-sm text-slate-500">
                    Página {Math.floor(pagination.skip / pagination.limit) + 1} de {Math.ceil(pagination.total / pagination.limit)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Histórico de Acessos */}
        <TabsContent value="logins" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Histórico de Login/Logout</CardTitle>
              <CardDescription>
                Últimos 30 dias de acessos ao sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loginHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Nenhum registro de acesso encontrado
                </div>
              ) : (
                <div className="space-y-2">
                  {loginHistory.map((log, index) => (
                    <div 
                      key={log._id || index}
                      className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <div className={`p-2 rounded-full ${
                        log.event_type === 'login' 
                          ? 'bg-green-100 text-green-600' 
                          : log.event_type === 'logout'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-red-100 text-red-600'
                      }`}>
                        {log.event_type === 'login' ? (
                          <LogIn className="w-4 h-4" />
                        ) : log.event_type === 'logout' ? (
                          <LogOut className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-800 dark:text-slate-200">
                          {log.user_name}
                        </div>
                        <div className="text-sm text-slate-500">
                          {log.user_email}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          log.event_type === 'login' 
                            ? 'bg-green-100 text-green-800' 
                            : log.event_type === 'logout'
                              ? 'bg-slate-100 text-slate-800'
                              : 'bg-red-100 text-red-800'
                        }>
                          {log.event_type === 'login' ? 'Entrou' : log.event_type === 'logout' ? 'Saiu' : 'Falhou'}
                        </Badge>
                        <div className="text-xs text-slate-400 mt-1">
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditLog;
